import React from 'react'
import { IScene } from '@/types/cinematics'
import { LAYOUT_TYPE } from '@/constants/auth'
import { useTranslation } from '@/localization/useTranslation'
import { useCinematicsStore } from '@/cinematics/stores/CinematicsStore'
import { resolveBackground } from '@/actors/AuthActor/theme/resolveBackground'
import { NextImage } from '@/components/NextImage'
import { LayoutType } from '../../types'

import classNames from 'classnames'
import classes from './style.module.scss'

interface AuthLayoutProps {
  type: LayoutType
  set?: IScene['set']
  style?: {
    background?: string,
    backgroundColor?: string
    sideImage?: string
  }
  /**
   * Theme tokens as CSS custom properties. Set on the scene root so every
   * component below inherits them — no per-component theming props.
   */
  vars?: React.CSSProperties
  /**
   * For pages that hide the navbar through `Layout`'s own prop rather than the
   * scene's `set` — the store cannot see that decision.
   */
  hasNavbar?: boolean
  children: React.ReactNode
}
interface VideoBackgroundWrapperProps {
  src: string,
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({ children, ...props }) => {
  const { type, set, style, vars, hasNavbar: hasNavbarProp } = props
  const { t } = useTranslation()
  const scene = useCinematicsStore((state) => state.scene)

  // The column layouts reserve navbar height; whatever hides the navbar must
  // reclaim it or a dead strip is left at the bottom. This deliberately mirrors
  // `Layout`'s own resolution exactly (store scene `set`, then the page prop) —
  // reading Director's `set` prop as well would diverge on scenes matched by
  // subpath or `defaultSceneId`, where the store holds no scene: Layout would
  // render the navbar while this reclaimed its height, overflowing the page.
  const hasNavbar = scene?.set?.navbar?.hasNavbar ?? hasNavbarProp ?? true
  const isIsland = type === LAYOUT_TYPE.ISLAND
  const videoSrc = set?.videos?.background
  const hasVideo = !!videoSrc && !isIsland

  const sideImageSrc = t(style?.sideImage || set?.images?.featured || '')
  const hasSideImage = !!sideImageSrc && !hasVideo && type === LAYOUT_TYPE.DOUBLE_COLUMN

  // `background` accepts an image URL or any CSS background value (gradients
  // included) — see resolveBackground for why the two cannot share one slot.
  const backgroundStyle = resolveBackground({
    background: t(style?.background || ''),
    backgroundColor: style?.backgroundColor,
    sceneImage: t(set?.images?.background || ''),
    hasVideo,
  })

  return (
    <section
      style={{ ...vars, ...backgroundStyle }}
      className={classNames({
        [classes.single]: type === LAYOUT_TYPE.SINGLE_COLUMN,
        [classes.double]: type === LAYOUT_TYPE.DOUBLE_COLUMN,
        [classes.island]: type === LAYOUT_TYPE.ISLAND,
        [classes.video]: hasVideo,
        [classes['no-navbar']]: !hasNavbar,
      })}
    >
      {hasVideo &&
        <VideoBackground src={videoSrc} />
      }
      <div className={classes.form}>
        {children}
      </div>
      {hasSideImage &&
        <NextImage
          className={classes['side-image']}
          src={sideImageSrc || ''}
          alt="Authentication image"
          width={600}
          height={400}
        />
      }
    </section>
  )
}

const VideoBackground: React.FC<VideoBackgroundWrapperProps> = (props) => {
  const { src } = props
  return (
    <video src={src} preload="auto" loop autoPlay muted playsInline />
  )
}